import { Link } from 'react-router-dom';

export function InsurancePage() {
  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <p className="text-orange font-semibold uppercase tracking-wider text-sm mb-2">
        Insurance &amp; coverage
      </p>
      <h1 className="text-3xl md:text-4xl font-bold text-navy mb-4">
        Your auto insurance does most of the work.
      </h1>
      <p className="text-lg text-navy-600 mb-10">
        In Canada, the policy on the tow vehicle generally extends to any trailer attached
        to it. That means most TowShare rentals are already covered by the renter's own
        insurance — and TowShare layers a $1,500 Trailer Guarantee on top.
      </p>

      <div className="space-y-6">
        <Section title="For renters">
          <ul className="list-disc pl-5 space-y-2 text-navy-700">
            <li>
              Your <strong>liability coverage</strong> extends to a trailer in tow. If you
              cause an accident while towing, your auto policy responds.
            </li>
            <li>
              Most Canadian auto policies also extend <strong>collision and comprehensive</strong>
              {' '}to attached utility trailers. Confirm with your insurer before your first booking.
            </li>
            <li>
              You're responsible for a <strong>refundable damage deposit</strong> on every
              booking (typically $200–$1,000, set by the owner). Held by Stripe and released
              24 hours after return if no claims.
            </li>
          </ul>
        </Section>

        <Section title="For owners">
          <ul className="list-disc pl-5 space-y-2 text-navy-700">
            <li>
              <strong>$1,500 TowShare Trailer Guarantee</strong> on every rental covers damage
              beyond the deposit and the renter's auto policy.
            </li>
            <li>
              <strong>Required pre- and post-rental photo handoff</strong> creates a tamper-proof
              record of condition.
            </li>
            <li>
              <strong>Renter ID verification</strong> via Stripe Identity is required before
              their first booking on the platform.
            </li>
            <li>
              Most personal trailers don't need separate commercial coverage to be listed.
              Confirm with your insurer if your trailer is used for business.
            </li>
          </ul>
        </Section>

        <Section title="Quick reference">
          <dl className="grid sm:grid-cols-2 gap-4 text-sm">
            <Row label="Primary coverage" value="Renter's auto policy" />
            <Row label="Damage deposit" value="Owner-set, Stripe pre-auth" />
            <Row label="TowShare top-up" value="Up to $1,500 per rental" />
            <Row label="ID verification" value="Stripe Identity" />
            <Row label="Payment processor" value="Stripe Connect (Express)" />
            <Row label="Dispute mediation" value="TowShare support team" />
          </dl>
        </Section>

        <Section title="What to do before your first rental">
          <ol className="list-decimal pl-5 space-y-2 text-navy-700">
            <li>Call your auto insurer and confirm trailer coverage while towing.</li>
            <li>Verify your ID on TowShare via Stripe Identity ({' '}
              <Link to="/account/verify" className="text-orange hover:underline">
                /account/verify
              </Link>
              ).
            </li>
            <li>Read the owner's listing rules — weight limits, allowed uses, return condition.</li>
            <li>Take pre-rental photos at pickup. We'll prompt you in the app.</li>
          </ol>
        </Section>

        <p className="text-sm text-navy-500">
          This page is informational and is not legal or insurance advice. TowShare doesn't
          sell insurance and isn't a licensed insurance broker. The $1,500 Trailer Guarantee
          is a TowShare-funded protection program subject to our{' '}
          <Link to="/terms" className="text-orange hover:underline">
            Terms &amp; Conditions
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card p-6">
      <h2 className="text-xl font-semibold text-navy mb-3">{title}</h2>
      {children}
    </section>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-navy-50 pb-2">
      <dt className="text-navy-500 text-xs uppercase tracking-wider">{label}</dt>
      <dd className="text-navy font-medium">{value}</dd>
    </div>
  );
}
